<script setup lang="ts">
    import { computed, ref, shallowRef } from 'vue';
    import {
        arrow,
        autoUpdate,
        flip,
        offset,
        shift,
        useFloating,
        type Placement,
    } from '@floating-ui/vue';
    import { temporaryUUID } from '../composables/useTemporaryUUID';

    const props = withDefaults(
        defineProps<{
            label: string;
            position?: 'top' | 'right';
            disabled?: boolean;
        }>(),
        {
            position: 'top',
            disabled: false,
        }
    );

    const tooltipId = 'echo-tooltip-' + temporaryUUID().replace('_', '');

    const reference = ref<HTMLElement | null>(null);
    const floating = shallowRef<HTMLElement | null>(null);
    const arrowEl = ref<HTMLElement | null>(null);
    const open = ref(false);

    const placement = computed<Placement>(() => (props.position === 'right' ? 'right' : 'top'));

    const {
        floatingStyles,
        middlewareData,
        placement: resolvedPlacement,
    } = useFloating(reference, floating, {
        placement,
        strategy: 'absolute',
        whileElementsMounted: autoUpdate,
        middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
    });

    const STATIC_SIDE: Record<string, string> = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
    };

    const arrowStyles = computed(() => {
        const data = middlewareData.value.arrow;
        if (!data) return {};
        const side = resolvedPlacement.value.split('-')[0];
        const staticSide = STATIC_SIDE[side];
        return {
            left: data.x != null ? `${data.x}px` : '',
            top: data.y != null ? `${data.y}px` : '',
            [staticSide]: '-4px',
        };
    });

    const show = () => {
        if (!props.disabled) open.value = true;
    };
    const hide = () => {
        open.value = false;
    };
</script>

<template>
    <span
        ref="reference"
        :aria-describedby="open ? tooltipId : undefined"
        @mouseenter="show"
        @mouseleave="hide"
        @focusin="show"
        @focusout="hide"
    >
        <slot />
        <Teleport to="body">
            <Transition name="popover">
                <div
                    v-if="open"
                    :id="tooltipId"
                    ref="floating"
                    class="Tooltip__tooltip"
                    :style="floatingStyles"
                    role="tooltip"
                >
                    {{ label }}
                    <div ref="arrowEl" class="Tooltip__arrow" :style="arrowStyles" />
                </div>
            </Transition>
        </Teleport>
    </span>
</template>
